import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Paper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Slider,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  ButtonGroup,
  Menu
} from '@mui/material';
import {
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  FormatAlignLeftOutlined,
  FormatAlignCenterOutlined,
  FormatAlignRightOutlined,
  Delete as DeleteIcon,
  Timeline as VectorIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import domtoimage from 'dom-to-image';
import { v4 as uuidv4 } from 'uuid';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import { BRAND } from '../../../config/brand';
import GuideButton from '../../common/guide/GuideButton';

// A5 boyutları (piksel olarak)
const a5Dimensions = {
  portrait: { width: 420, height: 595 }, // Dikey A5
  landscape: { width: 595, height: 420 } // Yatay A5
};

interface QrPrintModalProps {
  open: boolean;
  onClose: () => void;
  task: any;
}

const QrPrintModal: React.FC<QrPrintModalProps> = ({ open, onClose, task }) => {
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [pageColor, setPageColor] = useState('#ffffff');
  const printRef = useRef<HTMLDivElement>(null);
  const [qrSize, setQrSize] = useState(120);
  const [position, setPosition] = useState({ x: 150, y: 150 });
  const [dragging, setDragging] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [showQrCode, setShowQrCode] = useState(false);
  // Dosya girişi için ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Metin düzenleme için state'ler
  const [textContent, setTextContent] = useState('Yeni Metin');
  const [textFont, setTextFont] = useState('Roboto');
  const [textSize, setTextSize] = useState(16);
  const [textColor, setTextColor] = useState('#000000');
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  
  // Yeniden boyutlandırma işlemleri için gerekli state'ler
  const [resizing, setResizing] = useState(false);
  const [resizeItemId, setResizeItemId] = useState<string | null>(null);
  const [resizeStartPoint, setResizeStartPoint] = useState({ x: 0, y: 0 });
  const [initialWidth, setInitialWidth] = useState(0);
  
  // Yeni state'ler
  const [pageItems, setPageItems] = useState<Array<{
    id: string;
    type: 'qr' | 'logo' | 'text';
    position: { x: number, y: number };
    size?: number;
    width?: number; // Metin genişliği için yeni alan ekledik
    content?: string;
    imageUrl?: string;
    selected?: boolean;
    font?: string;
    fontSize?: number;
    color?: string;
    bold?: boolean;
    italic?: boolean;
    textAlign?: 'left' | 'center' | 'right';
  }>>([]);
  
  // Tasarım kaydetme ve seçme için eklenen state'ler
  const [saveLayoutDialogOpen, setSaveLayoutDialogOpen] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [savedLayouts, setSavedLayouts] = useState<Array<{id: string, name: string}>>([]);
  const [selectLayoutMenuAnchor, setSelectLayoutMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Vektörel indirme menüsü için state
  const [vectorMenuAnchor, setVectorMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Modal açıldığında lokalden kaydedilmiş düzeni yükle
  useEffect(() => {
    if (open && task) {
      // Debug bilgisi set etmeyi kaldırıyorum
      loadSavedLayout();
      loadSavedLayoutsList();
    }
  }, [open, task]);

  // Modal açıldığında otomatik QR kontrolü için state
  const [autoQrAdded, setAutoQrAdded] = useState(false);

  // Modal açıldığında QR kod otomatik kontrolü
  useEffect(() => {
    if (open && task && task.id && !autoQrAdded) {
      // Kısa gecikmeyle saved layout yüklenmesini bekle
      const timer = setTimeout(() => {
        // Eğer QR kod yoksa otomatik ekle
        setPageItems(currentItems => {
          const hasQrCode = currentItems.some(item => item.type === 'qr');
          if (!hasQrCode) {
            const newItem = {
              id: `qr-${Date.now()}`,
              type: 'qr' as const,
              position: { x: 200, y: 200 },
              size: qrSize,
              content: BRAND.urls.survey(task.id),
              selected: false
            };
            
            setShowQrCode(true);
            setAutoQrAdded(true);
            return [...currentItems, newItem];
          }
          setAutoQrAdded(true);
          return currentItems;
        });
      }, 200); // Layout yüklenmesi için 200ms bekle

      return () => clearTimeout(timer);
    }
    
    // Modal kapatıldığında flag'i sıfırla
    if (!open) {
      setAutoQrAdded(false);
    }
  }, [open, task, autoQrAdded, qrSize]);

  // Kaydedilmiş tasarımların listesini yükle
  const loadSavedLayoutsList = () => {
    try {
      const savedLayoutsStr = localStorage.getItem('savedQrLayouts');
      if (savedLayoutsStr) {
        const layouts = JSON.parse(savedLayoutsStr);
        setSavedLayouts(layouts);
      } else {
        setSavedLayouts([]);
      }
    } catch (error) {
      console.error('Kaydedilmiş tasarımlar yüklenirken hata:', error);
      setSavedLayouts([]);
    }
  };

  // Düzeni localStorage'dan yükle
  const loadSavedLayout = () => {
    if (!task || !task.id) return;

    try {
      const savedLayout = localStorage.getItem(`qrLayout_${task.id}`);
      if (savedLayout) {
        const layoutData = JSON.parse(savedLayout);
        setPageItems(layoutData.items || []);
        setOrientation(layoutData.orientation || 'portrait');
        setPageColor(layoutData.pageColor || '#ffffff');
        
        // QR kodu göster
        const hasQrCode = layoutData.items.some((item: any) => item.type === 'qr');
        setShowQrCode(hasQrCode);
      } else {
        // İlk defa açılıyorsa varsayılan değerleri ayarla
        setPageItems([]);
        setShowQrCode(false);
      }
    } catch (error) {
      console.error('Kaydedilmiş düzeni yükleme hatası:', error);
      setPageItems([]);
      setShowQrCode(false);
    }
  };

  // Düzeni localStorage'a kaydet
  const saveLayout = () => {
    if (!task || !task.id) return;

    try {
      const layoutData = {
        items: pageItems,
        orientation,
        pageColor
      };
      localStorage.setItem(`qrLayout_${task.id}`, JSON.stringify(layoutData));
    } catch (error) {
      console.error('Düzen kaydetme hatası:', error);
    }
  };

  // QR Kod ekleme fonksiyonu
  const handleAddQrCode = useCallback(() => {
    if (!task || !task.id) {
      console.error("Geçerli görev veya görev ID'si yok:", task);
      return;
    }
    
    // QR kodu pageItems'a ekle
    const newItem = {
      id: `qr-${Date.now()}`,
      type: 'qr' as const,
      position: { x: a5Dimensions.width / 2, y: a5Dimensions.height / 2 },
      size: qrSize,
      content: BRAND.urls.survey(task.id),
      selected: false
    };
    
    setPageItems(prev => [...prev, newItem]);
    setShowQrCode(true);
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  }, [task, qrSize]);

  // Metin ekleme işlevi
  const handleAddText = () => {
    const newId = uuidv4();
    setPageItems([...pageItems, {
      id: newId,
      type: 'text',
      position: { x: a5Dimensions.width / 2, y: a5Dimensions.height / 2 },
      content: 'Yeni Metin',
      font: 'Roboto',
      fontSize: 16,
      color: '#000000',
      bold: false,
      italic: false,
      textAlign: 'left',
      width: 200, // Varsayılan genişlik
      selected: true
    }]);
    
    // Sadece yeni eklenen öğe seçili olsun
    setPageItems(prev => prev.map(item => ({
      ...item,
      selected: item.id === newId
    })));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Metin içeriğini güncelleme
  const handleTextContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newContent = e.target.value;
    setTextContent(newContent);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          content: newContent
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı tipini güncelleme
  const handleFontChange = (event: SelectChangeEvent) => {
    const newFont = event.target.value;
    setTextFont(newFont);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          font: newFont
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı boyutunu güncelleme
  const handleFontSizeChange = (event: Event, newValue: number | number[]) => {
    const newSize = newValue as number;
    setTextSize(newSize);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          fontSize: newSize
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı rengini güncelleme
  const handleColorChange = (color: string) => {
    setTextColor(color);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          color: color
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Kalın yazıyı açıp kapatma
  const handleToggleBold = () => {
    const newBold = !textBold;
    setTextBold(newBold);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          bold: newBold
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // İtalik yazıyı açıp kapatma
  const handleToggleItalic = () => {
    const newItalic = !textItalic;
    setTextItalic(newItalic);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          italic: newItalic
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Öğe seçildiğinde düzenleme alanlarını güncelle
  useEffect(() => {
    const selectedItem = pageItems.find(item => item.selected && item.type === 'text');
    if (selectedItem && selectedItem.type === 'text') {
      // Text ayarlarını güncelle
      setTextContent(selectedItem.content || 'Metin');
      setTextFont(selectedItem.font || 'Roboto');
      setTextSize(selectedItem.fontSize || 16);
      setTextColor(selectedItem.color || '#000000');
      setTextBold(selectedItem.bold || false);
      setTextItalic(selectedItem.italic || false);
      // textAlign değerini de burada güncelle
    }
  }, [pageItems]);

  // Logo ekleme işlevi
  const handleAddLogo = () => {
    // Dosya seçme dialogunu aç
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Dosya yükleme fonksiyonu
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !task) return;
    
    // Sadece resim dosyalarını kabul et
    if (!file.type.startsWith('image/')) {
      alert('Lütfen sadece resim dosyası yükleyin (PNG, JPG, JPEG).');
      return;
    }
    
    // Resmi URL olarak oku
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      
      // Sayfaya logo ekle
      const newItem = {
        id: `logo-${Date.now()}`,
        type: 'logo' as const,
        position: { x: a5Dimensions.width / 2, y: a5Dimensions.height / 4 },
        size: 100,
        content: file.name,
        imageUrl,
        selected: false
      };
      
      setPageItems(prev => [...prev, newItem]);
      
      // Düzeni kaydet
      setTimeout(saveLayout, 100);
    };
    
    reader.readAsDataURL(file);
    
    // Input değerini sıfırla (aynı dosyayı tekrar seçebilmek için)
    event.target.value = '';
  };

  // Öğe seçme işlevi
  const handleSelectItem = (id: string) => {
    setPageItems(prev => prev.map(item => ({
      ...item,
      selected: item.id === id
    })));
  };

  // Seçili öğeyi silme işlevi
  const handleDeleteSelected = () => {
    setPageItems(prev => {
      const newItems = prev.filter(item => !item.selected);
      
      // QR kodu silindi mi kontrol et
      const hasQrCode = newItems.some(item => item.type === 'qr');
      if (!hasQrCode) {
        setShowQrCode(false);
      }
      
      // Düzeni kaydet
      setTimeout(saveLayout, 100);
      
      return newItems;
    });
  };

  // A5 boyutları (piksel olarak)
  const a5Dimensions = orientation === 'portrait' 
    ? { width: 420, height: 595 } // Dikey A5
    : { width: 595, height: 420 }; // Yatay A5

  // Sürükleme işlemleri
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    // Olayı durdurup varsayılan eylemleri engelle
    e.preventDefault();
    e.stopPropagation();
    
    // Öğeyi seç
    handleSelectItem(itemId);
    
    // Öğeyi sürükleme moduna al
    setDragging(true);
    
    // Seçilen öğenin pozisyonunu bul
    const item = pageItems.find(i => i.id === itemId);
    if (!item) return;
    
    // Tıklama noktası ile öğe konumu arasındaki farkı hesapla
    // Bu, sürükleme sırasında öğenin göreceli pozisyonunu korumamızı sağlar
    setStartPoint({
      x: e.clientX - item.position.x,
      y: e.clientY - item.position.y
    });
  };

  // Boyutlandırma başlangıç fonksiyonu
  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Öğeyi seç
    handleSelectItem(itemId);
    
    // Yeniden boyutlandırma modunu aç
    setResizing(true);
    setResizeItemId(itemId);
    
    // Başlangıç noktasını kaydet
    setResizeStartPoint({
      x: e.clientX,
      y: e.clientY
    });
    
    // Öğenin başlangıç genişliğini kaydet
    const item = pageItems.find(i => i.id === itemId);
    if (item) {
      setInitialWidth(item.width || 200);
    }
  };

  // Fare hareketi fonksiyonu - hem sürükleme hem boyutlandırma için
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Varsayılan eylemleri ve metin seçimini engelle
    e.preventDefault();
    e.stopPropagation();
    
    // Yeniden boyutlandırma modu
    if (resizing && resizeItemId) {
      const item = pageItems.find(i => i.id === resizeItemId);
      if (!item) return;
      
      // Hareket miktarını hesapla
      const dx = e.clientX - resizeStartPoint.x;
      
      // Minimum 100px, maksimum A5 genişliği kadar olabilir
      let newWidth = Math.max(100, Math.min(a5Dimensions.width - 40, initialWidth + dx));
      
      // Sadece genişliği güncelle, pozisyon ve diğer özellikleri koru
      setPageItems(prev => prev.map(item => {
        if (item.id === resizeItemId) {
          return {
            ...item,
            width: newWidth
          };
        }
        return item;
      }));
      return;
    }
    
    // Normal sürükleme modu
    if (!dragging) return;
    
    // Seçili öğeyi bul
    const selectedItem = pageItems.find(item => item.selected);
    if (!selectedItem) return;
    
    // Yeni pozisyonu hesapla
    const newX = e.clientX - startPoint.x;
    const newY = e.clientY - startPoint.y;
    
    // Sınırlar için kullanılacak değerleri hesapla
    let minEdgeDistance = 20; // Minimum kenar mesafesi
    
    if (selectedItem.type === 'qr') {
      minEdgeDistance = (selectedItem.size || qrSize) / 2;
    } else if (selectedItem.type === 'logo') {
      minEdgeDistance = (selectedItem.size || 100) / 2;
    } else if (selectedItem.type === 'text') {
      // Metin için daha büyük bir sınır kullan ki tamamen görünür kalsın
      minEdgeDistance = 50;
    }
    
    // A5 sayfa sınırları içinde kal
    const boundedX = Math.max(minEdgeDistance, Math.min(a5Dimensions.width - minEdgeDistance, newX));
    const boundedY = Math.max(minEdgeDistance, Math.min(a5Dimensions.height - minEdgeDistance, newY));
    
    // Öğenin SADECE pozisyonunu güncelle, boyutunu değiştirme
    setPageItems(prev => prev.map(item => {
      if (item.selected) {
        return {
          ...item,
          position: { x: boundedX, y: boundedY }
        };
      }
      return item;
    }));
  };

  // Fare bırakma fonksiyonu
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    // Yeniden boyutlandırma modunu kapat
    if (resizing) {
      setResizing(false);
      setResizeItemId(null);
      saveLayout(); // Değişiklikleri kaydet
      return;
    }
    
    // Normal sürükleme işlemi
    if (dragging) {
      // Varsayılan eylemleri ve metin seçimini engelle
      e.preventDefault();
      e.stopPropagation();
      
      // Sürükleme modunu kapat
      setDragging(false);
      
      // Değişiklikleri kaydet
      saveLayout();
    }
  };

  // Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!printRef.current) return;
    
    // Geçici olarak seçili öğeleri sakla
    const selectedItems = pageItems.filter(item => item.selected);
    
    try {
      // Seçimleri geçici olarak kaldır
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: false
      })));
      
      // DOM'un güncellenmesi için kısa bir gecikme ekle
      setTimeout(() => {
        if (!printRef.current) return;
        
        // dom-to-image ile A5 sayfasını JPG olarak oluştur
        domtoimage.toJpeg(printRef.current, { 
          quality: 0.95,
          bgcolor: pageColor,
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
            width: `${printRef.current.offsetWidth}px`,
            height: `${printRef.current.offsetHeight}px`
          }
        })
        .then(function (dataUrl) {
          // JPG'yi yazdır
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            alert('Lütfen popup pencerelerine izin verin.');
            
            // Seçimleri geri yükle
            setPageItems(prevItems => prevItems.map(item => ({
              ...item,
              selected: selectedItems.some(selItem => selItem.id === item.id)
            })));
            return;
          }
          
          const printCSS = `
            @page {
              size: ${orientation === 'portrait' ? 'A5 portrait' : 'A5 landscape'};
              margin: 0;
            }
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: white;
            }
            img {
              width: ${orientation === 'portrait' ? '148mm' : '210mm'};
              height: ${orientation === 'portrait' ? '210mm' : '148mm'};
              object-fit: contain;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          `;
          
          // Yazdırma penceresi içeriğini oluştur
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${task?.name || 'Görev'} QR Kodu</title>
                <style>${printCSS}</style>
              </head>
              <body>
                <img src="${dataUrl}" alt="QR Kod Sayfası" />
                <script>
                  window.onload = function() {
                    setTimeout(function() {
                      window.print();
                      window.close();
                    }, 500);
                  };
                </script>
              </body>
            </html>
          `);
          
          printWindow.document.close();
          
          // Seçimleri geri yükle
          setTimeout(() => {
            setPageItems(prevItems => prevItems.map(item => ({
              ...item,
              selected: selectedItems.some(selItem => selItem.id === item.id)
            })));
          }, 1000);
        })
        .catch(function (error) {
          console.error('Dom-to-image hatası:', error);
          alert('Sayfa görüntüsü oluşturulurken bir hata oluştu.');
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        });
      }, 300); // Daha uzun gecikme ile DOM'un tamamen yüklenmesini bekle
    } catch (error) {
      console.error('Yazdırma hatası:', error);
      alert('Yazdırma sırasında bir hata oluştu.');
      
      // Hata durumunda seçimleri geri yükle
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: selectedItems.some(selItem => selItem.id === item.id)
      })));
    }
  };

  // Yönlendirme değiştiğinde düzeni güncelle ve kaydet
  useEffect(() => {
    if (open && task) {
      saveLayout();
    }
  }, [orientation, pageColor, open, task]);

  // Tasarımı kaydet dialogunu aç
  const handleOpenSaveDialog = () => {
    setLayoutName('');
    setSaveLayoutDialogOpen(true);
  };

  // Tasarımı isimlendirerek kaydet
  const handleSaveNamedLayout = () => {
    if (!layoutName.trim()) return;
    
    try {
      const newLayoutId = `layout_${Date.now()}`;
      const layoutData = {
        items: pageItems,
        orientation,
        pageColor
      };
      
      // Yeni tasarımı kaydet
      localStorage.setItem(`qrLayout_${newLayoutId}`, JSON.stringify(layoutData));
      
      // Tasarım listesini güncelle
      const updatedLayouts = [...savedLayouts, { id: newLayoutId, name: layoutName }];
      localStorage.setItem('savedQrLayouts', JSON.stringify(updatedLayouts));
      setSavedLayouts(updatedLayouts);
      
      // Dialog'u kapat
      setSaveLayoutDialogOpen(false);
    } catch (error) {
      console.error('Tasarım kaydetme hatası:', error);
    }
  };

  // Tasarım seçme menüsünü aç
  const handleOpenSelectMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectLayoutMenuAnchor(event.currentTarget);
  };

  // Vektörel indirme menüsünü aç
  const handleOpenVectorMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setVectorMenuAnchor(event.currentTarget);
  };

  // Kaydedilmiş tasarımı yükle
  const handleLoadLayout = (layoutId: string) => {
    try {
      const savedLayout = localStorage.getItem(`qrLayout_${layoutId}`);
      if (savedLayout) {
        const layoutData = JSON.parse(savedLayout);
        setPageItems(layoutData.items || []);
        setOrientation(layoutData.orientation || 'portrait');
        setPageColor(layoutData.pageColor || '#ffffff');
        
        // QR kodu göster
        const hasQrCode = layoutData.items.some((item: any) => item.type === 'qr');
        setShowQrCode(hasQrCode);

        // Görev için seçilen tasarımı kaydet
        if (task && task.id) {
          localStorage.setItem(`qrLayout_${task.id}`, savedLayout);
        }
      }
    } catch (error) {
      console.error('Tasarım yükleme hatası:', error);
    }
    
    // Menüyü kapat
    setSelectLayoutMenuAnchor(null);
  };

  // Modal kapanırken güncel düzeni görev için kaydet
  const handleCloseWithSave = () => {
    if (task && task.id) {
      saveLayout(); // Mevcut tasarımı kaydet
    }
    onClose(); // Modalı kapat
  };

  // handleSaveImage fonksiyonunu ekle (yaklaşık 1200. satır civarında handlePrint fonksiyonundan sonra olmalı)
  const handleSaveImage = () => {
    if (!printRef.current) return;
    
    // Geçici olarak seçili öğeleri sakla
    const selectedItems = pageItems.filter(item => item.selected);
    
    try {
      // Seçimleri geçici olarak kaldır
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: false
      })));
      
      // DOM'un güncellenmesi için kısa bir gecikme ekle
      setTimeout(() => {
        if (!printRef.current) return;
        
        // dom-to-image ile A5 sayfasını JPG olarak oluştur
        domtoimage.toJpeg(printRef.current, { 
          quality: 0.95,
          bgcolor: pageColor,
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
            width: `${printRef.current.offsetWidth}px`,
            height: `${printRef.current.offsetHeight}px`
          }
        })
        .then(function (dataUrl) {
          // İndirme bağlantısı oluştur
          const link = document.createElement('a');
          link.download = `${task?.name || 'Görev'}_QR_Kodu.jpg`;
          link.href = dataUrl;
          link.click();
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        })
        .catch(function (error) {
          console.error('Dom-to-image hatası:', error);
          alert('Görüntü oluşturulurken bir hata oluştu.');
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        });
      }, 300);
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('Görüntü kaydedilirken bir hata oluştu.');
      
      // Hata durumunda seçimleri geri yükle
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: selectedItems.some(selItem => selItem.id === item.id)
      })));
    }
  };

  // Hizalama fonksiyonlarını ekle - handleToggleBold fonksiyonundan sonra
  const handleTextAlign = (align: 'left' | 'center' | 'right') => {
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          textAlign: align
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // QR kodu VEKTÖREL PDF olarak indirme fonksiyonu - SONSUZ KALİTE
  const handleDownloadQrPdf = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      console.log('PDF için manuel SVG oluşturma başlatılıyor...'); // Debug log

      // Manuel SVG oluşturma yöntemini kullan - GARANTILI SOLID RECTANGLES
      createManualSvg(qrUrl, 1000).then((svgString) => {
        console.log('PDF için manuel SVG oluşturuldu, dönüştürülüyor...'); // Debug log

        // PDF oluştur - A4 boyutunda
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // A4 boyutları (mm)
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // QR kodun boyutunu hesapla - sayfanın %80'i kadar (daha büyük)
        const qrSize = Math.min(pageWidth, pageHeight) * 0.8;
        
        // QR kodu sayfanın tam ortasına yerleştir
        const x = (pageWidth - qrSize) / 2;
        const y = (pageHeight - qrSize) / 2;

        // SVG'yi geçici bir img elementine yükle
        const img = new Image();
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = function() {
          // Geçici canvas oluştur - çok yüksek çözünürlük
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // 4K çözünürlük için 4096x4096
          canvas.width = 4096;
          canvas.height = 4096;
          
          // Beyaz arka plan
          if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // SVG'yi canvas'a çiz
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          }

          // Canvas'ı yüksek kaliteli PNG'ye çevir
          const imgData = canvas.toDataURL('image/png', 1.0);

          // QR kodu PDF'e ekle - SADECE QR, başka hiçbir şey yok
          pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);

          // PDF'i indir
          pdf.save(`${task.name || 'Görev'}_QR_Vektörel.pdf`);

          // URL'yi temizle
          URL.revokeObjectURL(url);
        };

        img.onerror = function() {
          console.error('SVG yükleme hatası');
          alert('QR kod görüntüsü oluşturulamadı.');
          URL.revokeObjectURL(url);
        };

        img.src = url;
      }).catch((error) => {
        console.error('Manuel PDF oluşturma hatası:', error);
        
        // Fallback: Normal SVG yöntemi
        console.log('Fallback PDF yöntemi deneniyor...');
        QRCode.toString(qrUrl, {
          type: 'svg',
          width: 1000,
          margin: 4,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H'
        }, function (error, svgString) {
          if (error) {
            console.error('Fallback PDF hatası:', error);
            alert('QR kod PDF oluşturulamadı.');
            return;
          }

          if (!svgString) {
            alert('QR kod PDF oluşturulamadı.');
            return;
          }

          // SVG'yi optimize et
          svgString = svgString.replace('<svg', '<svg shape-rendering="crispEdges"');

          // PDF oluştur - A4 boyutunda
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          });

          // A4 boyutları (mm)
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          // QR kodun boyutunu hesapla - sayfanın %80'i kadar
          const qrSize = Math.min(pageWidth, pageHeight) * 0.8;
          
          // QR kodu sayfanın tam ortasına yerleştir
          const x = (pageWidth - qrSize) / 2;
          const y = (pageHeight - qrSize) / 2;

          // SVG'yi geçici bir img elementine yükle
          const img = new Image();
          const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);

          img.onload = function() {
            // Geçici canvas oluştur
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            canvas.width = 2048;
            canvas.height = 2048;
            
            if (ctx) {
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            }

            const imgData = canvas.toDataURL('image/png', 1.0);
            pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);
            pdf.save(`${task.name || 'Görev'}_QR_Fallback_Vektörel.pdf`);
            URL.revokeObjectURL(url);
          };

          img.onerror = function() {
            console.error('Fallback PDF SVG yükleme hatası');
            alert('QR kod PDF oluşturulamadı.');
            URL.revokeObjectURL(url);
          };

          img.src = url;
        });
      });
    } catch (error) {
      console.error('PDF QR kod indirme hatası:', error);
      alert('PDF QR kod indirilirken hata oluştu.');
    }
  };

  // QR kodu ULTRA YÜKSEK çözünürlükte PNG indirme fonksiyonu - 4096x4096
  const handleDownloadQr = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      // Ultra yüksek çözünürlük canvas oluştur - 4096x4096
      const canvas = document.createElement('canvas');
      canvas.width = 4096;
      canvas.height = 4096;

      // Canvas context ayarları - keskin kenarlar için
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false; // Anti-aliasing'i kapat
        (ctx as any).webkitImageSmoothingEnabled = false;
        (ctx as any).mozImageSmoothingEnabled = false;
        (ctx as any).msImageSmoothingEnabled = false;
      }

      // QR kod verilerini ultra yüksek çözünürlükte canvas'a çiz - SOLID RECTANGLES
      QRCode.toCanvas(canvas, qrUrl, {
        width: 4096, // 4K çözünürlük
        margin: 8,   // Büyük margin
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // En yüksek hata düzeltme seviyesi
      }, function (error) {
        if (error) {
          console.error('QR kod oluşturma hatası:', error);
          alert('QR kod oluşturulurken hata oluştu.');
          return;
        }

        // Canvas'ı maksimum kaliteli PNG olarak indir
        canvas.toBlob((blob) => {
          if (!blob) {
            alert('QR kod görüntüsü oluşturulamadı.');
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${task.name || 'Görev'}_QR_Kod_4096x4096.png`;
          link.href = url;
          link.click();
          
          // URL'yi temizle
          URL.revokeObjectURL(url);
        }, 'image/png', 1.0); // Maksimum kalite (1.0)
      });
    } catch (error) {
      console.error('QR kod indirme hatası:', error);
      alert('QR kod indirilirken hata oluştu.');
    }
  };

  // Optimize edilmiş SVG oluşturma fonksiyonu - BÜYÜK SOLID RECTANGLES
  const createManualSvg = (qrUrl: string, size: number = 2000): Promise<string> => {
    return new Promise((resolve, reject) => {
      console.log('Optimize SVG oluşturma başlatıldı...');
      
      // Daha büyük canvas kullan - daha iyi çözünürlük için
      const canvas = document.createElement('canvas');
      canvas.width = 300;
      canvas.height = 300;
      
      // Canvas context ayarları - keskin kenarlar için
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        (ctx as any).webkitImageSmoothingEnabled = false;
        (ctx as any).mozImageSmoothingEnabled = false;
        (ctx as any).msImageSmoothingEnabled = false;
      }
      
      QRCode.toCanvas(canvas, qrUrl, {
        width: 300,
        margin: 4,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H'
      }, function (error) {
        if (error) {
          console.error('Canvas QR oluşturma hatası:', error);
          reject(error);
          return;
        }
        
        if (!ctx) {
          reject(new Error('Canvas context alınamadı'));
          return;
        }
        
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          console.log(`Canvas boyutu: ${canvas.width}x${canvas.height}`);
          
          // Siyah pikselleri matrix olarak çıkar
          const blackMatrix: boolean[][] = [];
          for (let y = 0; y < canvas.height; y++) {
            blackMatrix[y] = [];
            for (let x = 0; x < canvas.width; x++) {
              const pixelIndex = (y * canvas.width + x) * 4;
              const r = pixels[pixelIndex];
              const g = pixels[pixelIndex + 1];
              const b = pixels[pixelIndex + 2];
              const brightness = (r + g + b) / 3;
              blackMatrix[y][x] = brightness < 128;
            }
          }
          
          // Optimize edilmiş dikdörtgenleri bul
          const rectangles = findOptimizedRectangles(blackMatrix);
          
          const svgPixelSize = size / canvas.width;
          let svgContent = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">`;
          
          // Optimize edilmiş dikdörtgenleri SVG'ye ekle
          rectangles.forEach(rect => {
            const rectX = rect.x * svgPixelSize;
            const rectY = rect.y * svgPixelSize;
            const rectWidth = rect.width * svgPixelSize;
            const rectHeight = rect.height * svgPixelSize;
            
            svgContent += `<rect x="${rectX.toFixed(2)}" y="${rectY.toFixed(2)}" width="${rectWidth.toFixed(2)}" height="${rectHeight.toFixed(2)}" fill="#000000"/>`;
          });
          
          svgContent += '</svg>';
          
          console.log(`Optimize SVG oluşturuldu: ${rectangles.length} büyük dikdörtgen (${blackMatrix.flat().filter(Boolean).length} piksel yerine)`);
          resolve(svgContent);
          
        } catch (err) {
          console.error('SVG oluşturma hatası:', err);
          reject(err);
        }
      });
    });
  };

  // Siyah pikselleri büyük dikdörtgenlere optimize eden fonksiyon
  const findOptimizedRectangles = (matrix: boolean[][]): Array<{x: number, y: number, width: number, height: number}> => {
    const height = matrix.length;
    const width = matrix[0]?.length || 0;
    const visited: boolean[][] = Array(height).fill(null).map(() => Array(width).fill(false));
    const rectangles: Array<{x: number, y: number, width: number, height: number}> = [];
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (matrix[y][x] && !visited[y][x]) {
          // Yatay olarak ne kadar genişleyebiliriz?
          let maxWidth = 0;
          for (let w = x; w < width && matrix[y][w] && !visited[y][w]; w++) {
            maxWidth++;
          }
          
          // Dikey olarak ne kadar genişleyebiliriz?
          let maxHeight = 1;
          for (let h = y + 1; h < height; h++) {
            let canExpand = true;
            for (let w = x; w < x + maxWidth; w++) {
              if (!matrix[h][w] || visited[h][w]) {
                canExpand = false;
                break;
              }
            }
            if (canExpand) {
              maxHeight++;
            } else {
              break;
            }
          }
          
          // Bu dikdörtgeni işaretle
          for (let h = y; h < y + maxHeight; h++) {
            for (let w = x; w < x + maxWidth; w++) {
              visited[h][w] = true;
            }
          }
          
          rectangles.push({
            x: x,
            y: y,
            width: maxWidth,
            height: maxHeight
          });
        }
      }
    }
    
    return rectangles;
  };

  // QR kodu VEKTÖREL SVG formatında indirme fonksiyonu - SONSUZ KALİTE
  const handleDownloadQrSvg = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      console.log('Manuel SVG oluşturma başlatılıyor...'); // Debug log

      // Manuel SVG oluşturma yöntemini kullan - GARANTILI SOLID RECTANGLES
      createManualSvg(qrUrl, 2000).then((svgString) => {
        console.log('Manuel SVG oluşturuldu, indiriliyor...'); // Debug log

        // SVG dosyasını indir
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${task.name || 'Görev'}_QR_Manuel_Vektörel.svg`;
        link.href = url;
        link.click();
        
        // URL'yi temizle
        URL.revokeObjectURL(url);
      }).catch((error) => {
        console.error('Manuel SVG oluşturma hatası:', error);
        
        // Fallback: Normal SVG yöntemi
        console.log('Fallback SVG yöntemi deneniyor...');
        QRCode.toString(qrUrl, {
          type: 'svg',
          width: 2000,
          margin: 4,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H'
        }, function (error, svgString) {
          if (error) {
            console.error('Fallback SVG hatası:', error);
            alert('QR kod SVG oluşturulamadı.');
            return;
          }

          if (!svgString) {
            alert('QR kod SVG oluşturulamadı.');
            return;
          }

          // SVG'yi optimize et
          svgString = svgString.replace('<svg', '<svg shape-rendering="crispEdges"');

          // SVG dosyasını indir
          const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${task.name || 'Görev'}_QR_Fallback_Vektörel.svg`;
          link.href = url;
          link.click();
          
          URL.revokeObjectURL(url);
        });
      });
    } catch (error) {
      console.error('QR kod SVG indirme hatası:', error);
      alert('QR kod SVG indirilirken hata oluştu.');
    }
  };

  // QR kodu VEKTÖREL EPS formatında indirme fonksiyonu - PROFESYONEL BASKI KALİTESİ
  const handleDownloadQrEps = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      console.log('EPS için manuel SVG oluşturma başlatılıyor...'); // Debug log

      // Manuel SVG oluşturma yöntemini kullan - GARANTILI SOLID RECTANGLES
      createManualSvg(qrUrl, 2000).then((svgString) => {
        console.log('EPS için manuel SVG oluşturuldu, dönüştürülüyor...'); // Debug log

        // SVG'yi EPS formatına dönüştür
        const epsContent = convertSvgToEps(svgString, task.name || 'Görev');
        
        // EPS dosyasını indir
        const blob = new Blob([epsContent], { type: 'application/postscript' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${task.name || 'Görev'}_QR_Manuel_Vektörel.eps`;
        link.href = url;
        link.click();
        
        // URL'yi temizle
        URL.revokeObjectURL(url);
      }).catch((error) => {
        console.error('Manuel EPS oluşturma hatası:', error);
        
        // Fallback: Normal SVG yöntemi
        console.log('Fallback EPS yöntemi deneniyor...');
        QRCode.toString(qrUrl, {
          type: 'svg',
          width: 2000,
          margin: 4,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H'
        }, function (error, svgString) {
          if (error) {
            console.error('Fallback EPS hatası:', error);
            alert('QR kod EPS oluşturulamadı.');
            return;
          }

          if (!svgString) {
            alert('QR kod EPS oluşturulamadı.');
            return;
          }

          // SVG'yi optimize et
          svgString = svgString.replace('<svg', '<svg shape-rendering="crispEdges"');

          // SVG'yi EPS formatına dönüştür
          const epsContent = convertSvgToEps(svgString, task.name || 'Görev');
          
          // EPS dosyasını indir
          const blob = new Blob([epsContent], { type: 'application/postscript' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${task.name || 'Görev'}_QR_Fallback_Vektörel.eps`;
          link.href = url;
          link.click();
          
          URL.revokeObjectURL(url);
        });
      });
    } catch (error) {
      console.error('QR kod EPS indirme hatası:', error);
      alert('QR kod EPS indirilirken hata oluştu.');
    }
  };

  // QR kodu ULTRA HD TIF formatında indirme fonksiyonu - PROFESYONEL BASKI KALİTESİ
  const handleDownloadQrTif = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      // ULTRA YÜKSEK ÇÖZÜNÜRLÜK için 4K canvas
      const canvas = document.createElement('canvas');
      const ultraHdSize = 4096; // 4K çözünürlük
      canvas.width = ultraHdSize;
      canvas.height = ultraHdSize;

      // Canvas context ayarları - keskin kenarlar için
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false; // Anti-aliasing'i kapat
        (ctx as any).webkitImageSmoothingEnabled = false;
        (ctx as any).mozImageSmoothingEnabled = false;
        (ctx as any).msImageSmoothingEnabled = false;
      }

      QRCode.toCanvas(canvas, qrUrl, {
        width: ultraHdSize,
        margin: 8, // Büyük margin
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // En yüksek hata düzeltme
      }, function (error) {
        if (error) {
          console.error('QR kod oluşturma hatası:', error);
          alert('QR kod oluşturulurken hata oluştu.');
          return;
        }

        // Canvas'ı TIFF formatına dönüştür ve indir
        createUltraHdTiff(canvas, task.name || 'Görev');
      });
    } catch (error) {
      console.error('QR kod TIF indirme hatası:', error);
      alert('QR kod TIF indirilirken hata oluştu.');
    }
  };

  // QR kodu VEKTÖREL DXF formatında indirme fonksiyonu - CAD/TEKNİK ÇİZİM KALİTESİ
  const handleDownloadQrDxf = () => {
    if (!task || !task.id) {
      alert('Geçerli görev bulunamadı.');
      return;
    }

    // QR kodun sayfada mevcut olup olmadığını kontrol et
    const hasQrCode = pageItems.some(item => item.type === 'qr');
    if (!hasQrCode) {
      alert('Önce QR kod eklemelisiniz.');
      return;
    }

    try {
      // QR kodun URL'sini oluştur
      const qrUrl = BRAND.urls.survey(task.id);

      console.log('DXF için manuel SVG oluşturma başlatılıyor...'); // Debug log

      // Manuel SVG oluşturma yöntemini kullan - GARANTILI SOLID RECTANGLES
      createManualSvg(qrUrl, 2000).then((svgString) => {
        console.log('DXF için manuel SVG oluşturuldu, dönüştürülüyor...'); // Debug log

        // SVG'yi DXF formatına dönüştür
        const dxfContent = convertSvgToDxf(svgString, task.name || 'Görev');
        
        // DXF dosyasını indir
        const blob = new Blob([dxfContent], { type: 'application/dxf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${task.name || 'Görev'}_QR_Manuel_Vektörel.dxf`;
        link.href = url;
        link.click();
        
        // URL'yi temizle
        URL.revokeObjectURL(url);
      }).catch((error) => {
        console.error('Manuel DXF oluşturma hatası:', error);
        
        // Fallback: Canvas tabanlı yöntem
        console.log('Fallback DXF yöntemi deneniyor...');
        
        // ULTRA YÜKSEK KALİTE için gerçek QR kod matrisini al
        // Canvas kullanarak QR kod piksellerini çıkar
        const canvas = document.createElement('canvas');
        const qrSize = 1000; // Çok yüksek çözünürlük
        canvas.width = qrSize;
        canvas.height = qrSize;

        // Canvas context ayarları - keskin kenarlar için
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = false; // Anti-aliasing'i kapat
          (ctx as any).webkitImageSmoothingEnabled = false;
          (ctx as any).mozImageSmoothingEnabled = false;
          (ctx as any).msImageSmoothingEnabled = false;
        }

        QRCode.toCanvas(canvas, qrUrl, {
          width: qrSize,
          margin: 4,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H' // En yüksek hata düzeltme
        }, function (error) {
          if (error) {
            console.error('Fallback DXF hatası:', error);
            alert('QR kod DXF oluşturulamadı.');
            return;
          }

          // Canvas'tan piksel verilerini çıkar ve DXF'e dönüştür
          const dxfContent = createUltraHighQualityDxf(canvas, task.name || 'Görev');
          
          // DXF dosyasını indir
          const blob = new Blob([dxfContent], { type: 'application/dxf' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${task.name || 'Görev'}_QR_Fallback_UltraHD.dxf`;
          link.href = url;
          link.click();
          
          // URL'yi temizle
          URL.revokeObjectURL(url);
        });
      });
    } catch (error) {
      console.error('QR kod DXF indirme hatası:', error);
      alert('QR kod DXF indirilirken hata oluştu.');
    }
  };

  // SVG'yi DXF formatına dönüştürme fonksiyonu - SOLID RECTANGLES
  const convertSvgToDxf = (svgString: string, taskName: string): string => {
    // SVG'den boyut bilgilerini çıkar
    const widthMatch = svgString.match(/width="(\d+)"/);
    const heightMatch = svgString.match(/height="(\d+)"/);
    const width = widthMatch ? parseInt(widthMatch[1]) : 2000;
    const height = heightMatch ? parseInt(heightMatch[1]) : 2000;

    // Güvenli dosya adı oluştur
    const safeTaskName = taskName.replace(/[^a-zA-Z0-9]/g, '_');
    
    // DXF header - AutoCAD uyumlu
    let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1014
9
$EXTMIN
10
0.0
20
0.0
30
0.0
9
$EXTMAX
10
${width}.0
20
${height}.0
30
0.0
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
5
2
330
0
100
AcDbSymbolTable
70
2
0
LAYER
5
10
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
0
70
0
62
7
6
CONTINUOUS
0
LAYER
5
11
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
QR_SOLID
70
0
62
1
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;

    // SVG rect'lerini DXF SOLID entity'lerine dönüştür
    const rectMatches = svgString.match(/<rect[^>]*>/g) || [];
    let entityHandle = 100;
    let entityCount = 0;
    
    rectMatches.forEach(rect => {
      const xMatch = rect.match(/x="([^"]*)"/);
      const yMatch = rect.match(/y="([^"]*)"/);
      const widthMatch = rect.match(/width="([^"]*)"/);
      const heightMatch = rect.match(/height="([^"]*)"/);
      
      if (xMatch && yMatch && widthMatch && heightMatch) {
        const x = parseFloat(xMatch[1]);
        const y = parseFloat(yMatch[1]);
        const w = parseFloat(widthMatch[1]);
        const h = parseFloat(heightMatch[1]);
        
        // DXF koordinat sisteminde Y ekseni ters olduğu için dönüştür
        const dxfY = height - y - h;
        
        // SOLID entity oluştur (dolu dikdörtgen)
        dxfContent += `0
SOLID
5
${entityHandle.toString(16).toUpperCase()}
100
AcDbEntity
8
QR_SOLID
100
AcDbTrace
10
${x.toFixed(4)}
20
${dxfY.toFixed(4)}
30
0.0
11
${(x + w).toFixed(4)}
21
${dxfY.toFixed(4)}
31
0.0
12
${(x + w).toFixed(4)}
22
${(dxfY + h).toFixed(4)}
32
0.0
13
${x.toFixed(4)}
23
${(dxfY + h).toFixed(4)}
33
0.0
`;
        entityHandle++;
        entityCount++;
      }
    });
    
    console.log(`DXF oluşturuldu: ${entityCount} SOLID entity`);

    // DXF footer
    dxfContent += `0
ENDSEC
0
EOF`;

    return dxfContent;
  };

  // SVG'yi EPS formatına dönüştürme fonksiyonu
  const convertSvgToEps = (svgString: string, taskName: string): string => {
    // SVG'den boyut bilgilerini çıkar
    const widthMatch = svgString.match(/width="(\d+)"/);
    const heightMatch = svgString.match(/height="(\d+)"/);
    const width = widthMatch ? parseInt(widthMatch[1]) : 2000;
    const height = heightMatch ? parseInt(heightMatch[1]) : 2000;

    // QR kod verilerini SVG'den çıkar (basit path parsing)
    const pathMatches = svgString.match(/<path[^>]*d="([^"]*)"[^>]*>/g) || [];
    
    // EPS header
    let epsContent = `%!PS-Adobe-3.0 EPSF-3.0
%%Title: ${taskName} QR Code
%%Creator: GTS QR Print Modal
%%BoundingBox: 0 0 ${width} ${height}
%%HiResBoundingBox: 0.0 0.0 ${width}.0 ${height}.0
%%DocumentData: Clean7Bit
%%LanguageLevel: 2
%%Pages: 1
%%EndComments

%%BeginProlog
/M {moveto} bind def
/L {lineto} bind def
/C {curveto} bind def
/Z {closepath} bind def
/F {fill} bind def
/S {stroke} bind def
%%EndProlog

%%Page: 1 1
gsave
0 0 0 setrgbcolor  % Siyah renk
newpath

`;

    // SVG rect'lerini EPS path'lerine dönüştür
    const rectMatches = svgString.match(/<rect[^>]*>/g) || [];
    rectMatches.forEach(rect => {
      const xMatch = rect.match(/x="([^"]*)"/);
      const yMatch = rect.match(/y="([^"]*)"/);
      const widthMatch = rect.match(/width="([^"]*)"/);
      const heightMatch = rect.match(/height="([^"]*)"/);
      
      if (xMatch && yMatch && widthMatch && heightMatch) {
        const x = parseFloat(xMatch[1]);
        const y = parseFloat(yMatch[1]);
        const w = parseFloat(widthMatch[1]);
        const h = parseFloat(heightMatch[1]);
        
        // EPS koordinat sisteminde Y ekseni ters olduğu için dönüştür
        const epsY = height - y - h;
        
        epsContent += `${x} ${epsY} M
${x + w} ${epsY} L
${x + w} ${epsY + h} L
${x} ${epsY + h} L
Z F
`;
      }
    });

    // EPS footer
    epsContent += `
grestore
showpage
%%EOF`;

    return epsContent;
  };

  // QR kod matrisinden doğrudan DXF oluşturma fonksiyonu - DAHA ETKİLİ
  const createDxfFromQrMatrix = (qr: any, taskName: string): string => {
    const modules = qr.modules;
    const size = modules.size;
    const moduleSize = 10; // Her modül 10 birim olsun
    const totalSize = size * moduleSize;
    
    console.log('QR Matrix size:', size); // Debug için
    console.log('Total modules:', size * size); // Debug için

    // DXF header - Basitleştirilmiş
    let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1014
9
$EXTMIN
10
0.0
20
0.0
30
0.0
9
$EXTMAX
10
${totalSize}.0
20
${totalSize}.0
30
0.0
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
5
2
330
0
100
AcDbSymbolTable
70
1
0
LAYER
5
10
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
0
70
0
62
7
6
CONTINUOUS
0
LAYER
5
11
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
QR_CODE
70
0
62
1
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;

    let entityCount = 0;
    let entityHandle = 100;

    // QR kod matrisini tarayıp siyah modülleri DXF SOLID entity'lerine dönüştür
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        // Modül siyah mı kontrol et
        if (modules.get(x, y)) {
          const x1 = x * moduleSize;
          const y1 = (size - y - 1) * moduleSize; // Y koordinatını ters çevir
          const x2 = x1 + moduleSize;
          const y2 = y1 + moduleSize;

          // SOLID entity oluştur (dolu dikdörtgen)
          dxfContent += `0
SOLID
5
${entityHandle.toString(16).toUpperCase()}
100
AcDbEntity
8
QR_CODE
100
AcDbTrace
10
${x1.toFixed(1)}
20
${y1.toFixed(1)}
30
0.0
11
${x2.toFixed(1)}
21
${y1.toFixed(1)}
31
0.0
12
${x2.toFixed(1)}
22
${y2.toFixed(1)}
32
0.0
13
${x1.toFixed(1)}
23
${y2.toFixed(1)}
33
0.0
`;
          entityHandle++;
          entityCount++;
        }
      }
    }

    console.log('DXF entities created:', entityCount); // Debug için

    // DXF footer
    dxfContent += `0
ENDSEC
0
EOF`;

    return dxfContent;
  };

  // ULTRA HD TIFF oluşturma fonksiyonu - 4K çözünürlük
  const createUltraHdTiff = (canvas: HTMLCanvasElement, taskName: string) => {
    const width = canvas.width;
    const height = canvas.height;
    
    console.log(`Ultra HD TIFF oluşturuluyor: ${width}x${height} piksel`);

    // Canvas'tan yüksek kaliteli PNG data URL al
    const dataUrl = canvas.toDataURL('image/png', 1.0); // Maksimum kalite
    
    // PNG'yi TIFF formatına dönüştür (basit TIFF header ile)
    const tiffData = createTiffFromCanvas(canvas, taskName);
    
    // TIFF dosyasını indir
    const blob = new Blob([tiffData], { type: 'image/tiff' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${taskName}_QR_UltraHD_4096x4096.tif`;
    link.href = url;
    link.click();
    
    // URL'yi temizle
    URL.revokeObjectURL(url);
    
    console.log(`TIFF dosyası indirildi: ${Math.round(blob.size / 1024)}KB`);
  };

  // Canvas'tan TIFF formatı oluşturma fonksiyonu
  const createTiffFromCanvas = (canvas: HTMLCanvasElement, taskName: string): ArrayBuffer => {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context alınamadı');
    }

    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;

    // TIFF header oluştur (Little Endian)
    const tiffHeader = new ArrayBuffer(8);
    const headerView = new DataView(tiffHeader);
    
    // TIFF signature
    headerView.setUint16(0, 0x4949, true); // "II" - Little Endian
    headerView.setUint16(2, 42, true);     // TIFF version
    headerView.setUint32(4, 8, true);      // IFD offset

    // IFD (Image File Directory) oluştur
    const ifdEntryCount = 12; // Tag sayısı
    const ifdSize = 2 + (ifdEntryCount * 12) + 4; // Entry count + entries + next IFD offset
    const ifd = new ArrayBuffer(ifdSize);
    const ifdView = new DataView(ifd);
    
    let offset = 0;
    
    // IFD entry count
    ifdView.setUint16(offset, ifdEntryCount, true);
    offset += 2;

    // Helper function for IFD entries
    const addIfdEntry = (tag: number, type: number, count: number, value: number) => {
      ifdView.setUint16(offset, tag, true);     // Tag
      ifdView.setUint16(offset + 2, type, true); // Type
      ifdView.setUint32(offset + 4, count, true); // Count
      ifdView.setUint32(offset + 8, value, true); // Value/Offset
      offset += 12;
    };

    // TIFF tags
    addIfdEntry(256, 4, 1, width);           // ImageWidth
    addIfdEntry(257, 4, 1, height);          // ImageLength
    addIfdEntry(258, 3, 1, 8);               // BitsPerSample (8-bit)
    addIfdEntry(259, 3, 1, 1);               // Compression (none)
    addIfdEntry(262, 3, 1, 1);               // PhotometricInterpretation (BlackIsZero)
    addIfdEntry(273, 4, 1, 8 + ifdSize);     // StripOffsets (data starts after header + IFD)
    addIfdEntry(277, 3, 1, 1);               // SamplesPerPixel (grayscale)
    addIfdEntry(278, 4, 1, height);          // RowsPerStrip
    addIfdEntry(279, 4, 1, width * height);  // StripByteCounts
    addIfdEntry(282, 5, 1, 8 + ifdSize + width * height); // XResolution offset
    addIfdEntry(283, 5, 1, 8 + ifdSize + width * height + 8); // YResolution offset
    addIfdEntry(296, 3, 1, 2);               // ResolutionUnit (inches)

    // Next IFD offset (0 = no more IFDs)
    ifdView.setUint32(offset, 0, true);

    // Grayscale image data oluştur (RGB'den grayscale'e dönüştür)
    const grayscaleData = new Uint8Array(width * height);
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      // Grayscale conversion
      const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      grayscaleData[i / 4] = gray;
    }

    // Resolution data (300 DPI)
    const resolutionData = new ArrayBuffer(16);
    const resView = new DataView(resolutionData);
    resView.setUint32(0, 300, true);  // XResolution numerator
    resView.setUint32(4, 1, true);    // XResolution denominator
    resView.setUint32(8, 300, true);  // YResolution numerator
    resView.setUint32(12, 1, true);   // YResolution denominator

    // Tüm parçaları birleştir
    const totalSize = tiffHeader.byteLength + ifd.byteLength + grayscaleData.byteLength + resolutionData.byteLength;
    const tiffFile = new ArrayBuffer(totalSize);
    const tiffView = new Uint8Array(tiffFile);
    
    let pos = 0;
    
    // Header
    tiffView.set(new Uint8Array(tiffHeader), pos);
    pos += tiffHeader.byteLength;
    
    // IFD
    tiffView.set(new Uint8Array(ifd), pos);
    pos += ifd.byteLength;
    
    // Image data
    tiffView.set(grayscaleData, pos);
    pos += grayscaleData.byteLength;
    
    // Resolution data
    tiffView.set(new Uint8Array(resolutionData), pos);

    return tiffFile;
  };

  // ULTRA YÜKSEK KALİTE DXF oluşturma fonksiyonu - Gerçek QR kod piksellerinden
  const createUltraHighQualityDxf = (canvas: HTMLCanvasElement, taskName: string): string => {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context alınamadı');
    }

    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    
    // Piksel analizi için sampling oranı (performans için)
    const sampleRate = 2; // Her 2 pikselde bir örnekle
    const moduleSize = 1.0; // Çok küçük modül boyutu = ultra detay
    
    console.log(`Ultra HD DXF: ${width}x${height} piksel analiz ediliyor...`);

    // DXF header - Ultra HD için optimize
    let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1014
9
$INSUNITS
70
4
9
$EXTMIN
10
0.0
20
0.0
30
0.0
9
$EXTMAX
10
${width * moduleSize}.0
20
${height * moduleSize}.0
30
0.0
9
$LIMMIN
10
0.0
20
0.0
9
$LIMMAX
10
${width * moduleSize}.0
20
${height * moduleSize}.0
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
5
2
330
0
100
AcDbSymbolTable
70
2
0
LAYER
5
10
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
0
70
0
62
7
6
CONTINUOUS
370
25
0
LAYER
5
11
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
QR_ULTRA_HD
70
0
62
1
6
CONTINUOUS
370
18
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;

    let entityHandle = 1000;
    let entityCount = 0;
    let blackPixelCount = 0;

    // Piksel tarama - her siyah piksel için bir SOLID entity
    for (let y = 0; y < height; y += sampleRate) {
      for (let x = 0; x < width; x += sampleRate) {
        const pixelIndex = (y * width + x) * 4;
        const r = pixels[pixelIndex];
        const g = pixels[pixelIndex + 1];
        const b = pixels[pixelIndex + 2];
        
        // Siyah piksel kontrolü (threshold ile)
        const brightness = (r + g + b) / 3;
        if (brightness < 128) { // Siyah veya koyu gri
          blackPixelCount++;
          
          // DXF koordinatları (Y ekseni ters)
          const x1 = x * moduleSize;
          const y1 = (height - y - sampleRate) * moduleSize;
          const x2 = x1 + (sampleRate * moduleSize);
          const y2 = y1 + (sampleRate * moduleSize);

          // Çok küçük SOLID entity oluştur
          dxfContent += `0
SOLID
5
${entityHandle.toString(16).toUpperCase()}
100
AcDbEntity
8
QR_ULTRA_HD
62
0
100
AcDbTrace
10
${x1.toFixed(3)}
20
${y1.toFixed(3)}
30
0.0
11
${x2.toFixed(3)}
21
${y1.toFixed(3)}
31
0.0
12
${x2.toFixed(3)}
22
${y2.toFixed(3)}
32
0.0
13
${x1.toFixed(3)}
23
${y2.toFixed(3)}
33
0.0
`;
          entityHandle++;
          entityCount++;
          
          // Performans için batch işleme
          if (entityCount % 1000 === 0) {
            console.log(`İşlenen entity sayısı: ${entityCount}`);
          }
        }
      }
    }

    console.log(`Ultra HD DXF tamamlandı:`);
    console.log(`- Toplam siyah piksel: ${blackPixelCount}`);
    console.log(`- Oluşturulan entity: ${entityCount}`);
    console.log(`- Dosya boyutu tahmini: ${Math.round(entityCount * 0.2)}KB`);

    // DXF footer
    dxfContent += `0
ENDSEC
0
EOF`;

    return dxfContent;
  };

  // Basit DXF QR kod oluşturma fonksiyonu - Test amaçlı (ESKİ)
  const createSimpleDxfQr = (qrUrl: string, size: number, moduleSize: number, taskName: string): string => {
    const totalSize = size * moduleSize;
    
    console.log('Creating simple DXF QR with size:', size); // Debug için

    // DXF header - Basitleştirilmiş
    let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1014
9
$EXTMIN
10
0.0
20
0.0
30
0.0
9
$EXTMAX
10
${totalSize}.0
20
${totalSize}.0
30
0.0
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
5
2
330
0
100
AcDbSymbolTable
70
1
0
LAYER
5
10
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
0
70
0
62
7
6
CONTINUOUS
0
LAYER
5
11
330
2
100
AcDbSymbolTableRecord
100
AcDbLayerTableRecord
2
QR_CODE
70
0
62
1
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;

    let entityHandle = 100;
    let entityCount = 0;

    // Basit QR kod deseni oluştur - test amaçlı
    // Gerçek QR kod yerine basit bir desen
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        // Basit QR kod benzeri desen
        let shouldFill = false;
        
        // Köşe kareleri (finder patterns)
        if ((x < 7 && y < 7) || (x >= size - 7 && y < 7) || (x < 7 && y >= size - 7)) {
          shouldFill = (x === 0 || x === 6 || y === 0 || y === 6 || 
                       (x >= 2 && x <= 4 && y >= 2 && y <= 4));
        }
        // Timing patterns
        else if (x === 6 || y === 6) {
          shouldFill = (x + y) % 2 === 0;
        }
        // Veri alanı - rastgele desen
        else {
          shouldFill = ((x + y + x * y) % 3) === 0;
        }

        if (shouldFill) {
          const x1 = x * moduleSize;
          const y1 = (size - y - 1) * moduleSize; // Y koordinatını ters çevir
          const x2 = x1 + moduleSize;
          const y2 = y1 + moduleSize;

          // SOLID entity oluştur (dolu dikdörtgen)
          dxfContent += `0
SOLID
5
${entityHandle.toString(16).toUpperCase()}
100
AcDbEntity
8
QR_CODE
100
AcDbTrace
10
${x1.toFixed(1)}
20
${y1.toFixed(1)}
30
0.0
11
${x2.toFixed(1)}
21
${y1.toFixed(1)}
31
0.0
12
${x2.toFixed(1)}
22
${y2.toFixed(1)}
32
0.0
13
${x1.toFixed(1)}
23
${y2.toFixed(1)}
33
0.0
`;
          entityHandle++;
          entityCount++;
        }
      }
    }

    console.log('Simple DXF entities created:', entityCount); // Debug için

    // DXF footer
    dxfContent += `0
ENDSEC
0
EOF`;

    return dxfContent;
  };


  if (!task) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCloseWithSave} // onClose yerine handleCloseWithSave kullan
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight="bold">QR Kod Sayfası Tasarla</Typography>
          <GuideButton guideKey="tasks.qrPrint" size="small" color="inherit" />
        </Box>
        <IconButton onClick={handleCloseWithSave} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
          {/* Sol Bölüm - A5 Tasarım Alanı */}
          <Box 
            sx={{ 
              flex: 2, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'grey.100',
              overflow: 'auto'
            }}
          >
            <Paper
              ref={printRef}
              elevation={3}
              sx={{
                width: a5Dimensions.width,
                height: a5Dimensions.height,
                backgroundColor: pageColor,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                userSelect: 'none', // Tüm içerik için metin seçimini engelle
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* A5 sayfasına eklenen öğeler */}
              {pageItems.map(item => {
                if (item.type === 'qr' && task.id) {
                  return (
                    <Box 
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)',
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        backgroundColor: '#ffffff',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Metin seçimini engelle
                        handleMouseDown(e, item.id);
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      <QRCodeCanvas 
                        value={BRAND.urls.survey(task.id)}
                        size={item.size || qrSize}
                        level="H" // Yüksek hata düzeltme seviyesi
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        id={`qr-${item.id}`}
                      />
                    </Box>
                  );
                } else if (item.type === 'text') {
                  return (
                    <Box
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)', // Merkezi hizalama korundu
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        padding: '10px',
                        background: item.selected ? 'rgba(255,255,255,0.9)' : 'transparent',
                        borderRadius: '4px',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1,
                        userSelect: 'none', // Metin seçimini engelle
                        WebkitUserSelect: 'none', // Safari için
                        MozUserSelect: 'none', // Firefox için
                        msUserSelect: 'none', // IE/Edge için
                        width: `${item.width || 200}px`, // Dinamik genişlik kullan
                        boxSizing: 'border-box', // Genişliğin padding dahil olması için
                        '&:hover': {
                          outline: item.selected ? '1px dashed #1976d2' : 'none',
                        },
                        '&:hover::after': item.selected ? {
                          content: '""',
                          position: 'absolute',
                          right: '-4px',
                          bottom: '-4px',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          borderRadius: '50%',
                          cursor: 'nwse-resize'
                        } : {}
                      }}
                      onMouseDown={(e) => {
                        // Varsayılan metin seçme davranışını engelle
                        e.preventDefault();
                        
                        // Köşe noktasından yeniden boyutlandırma
                        const rect = e.currentTarget.getBoundingClientRect();
                        const isResizeHandle = 
                          e.clientX > rect.right - 15 && 
                          e.clientY > rect.bottom - 15;
                        
                        if (isResizeHandle) {
                          // Yeniden boyutlandırma modu aktif
                          handleResizeStart(e, item.id);
                        } else {
                          // Normal sürükleme modu
                          handleMouseDown(e, item.id);
                        }
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      <Typography 
                        variant="body1"
                        sx={{
                          fontFamily: item.font || 'Roboto',
                          fontSize: `${item.fontSize || 16}px`,
                          color: item.color || '#000000',
                          fontWeight: item.bold ? 'bold' : 'normal',
                          fontStyle: item.italic ? 'italic' : 'normal',
                          whiteSpace: 'pre-wrap',
                          pointerEvents: 'none', // İçeriğin fare etkileşimini engelle
                          textAlign: item.textAlign || 'left', // Metin hizalama özelliğini ekle
                          width: '100%', // Yüzde 100 genişlik belirledik
                          display: 'block' // Blok element olarak görüntülenecek
                        }}
                      >
                        {item.content || 'Metin'}
                      </Typography>
                    </Box>
                  );
                } else if (item.type === 'logo') {
                  return (
                    <Box
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)',
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        width: `${item.size}px`,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1,
                        overflow: 'hidden'
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Metin seçimini engelle
                        handleMouseDown(e, item.id);
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      {item.imageUrl ? (
                        <img 
                          src={item.imageUrl} 
                          alt={item.content || 'Logo'} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            pointerEvents: 'none' // Resim etkileşimini engelle
                          }} 
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      )}
                    </Box>
                  );
                }
                return null;
              })}
            </Paper>
          </Box>

          {/* Sağ Bölüm - Araçlar */}
          <Box sx={{ 
            flex: 1, 
            borderLeft: '1px solid', 
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowY: 'auto'
          }}>
            {/* Sayfa Yönlendirmesi */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Sayfa Yönlendirmesi
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup 
                  row 
                  value={orientation} 
                  onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
                >
                  <FormControlLabel 
                    value="portrait" 
                    control={<Radio />} 
                    label="Dikey" 
                  />
                  <FormControlLabel 
                    value="landscape" 
                    control={<Radio />} 
                    label="Yatay" 
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider />

            {/* Seçili öğeyi silme butonu */}
            {pageItems.some(item => item.selected) && (
              <Box>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  sx={{ py: 1.5, mb: 2 }}
                  onClick={handleDeleteSelected}
                >
                  Seçili Öğeyi Sil
                </Button>
              </Box>
            )}

            {/* QR Ekle/Kaldır Butonu */}
            <Box>
              <Button
                variant="outlined"
                color={pageItems.some(item => item.type === 'qr') ? "error" : "primary"}
                fullWidth
                startIcon={pageItems.some(item => item.type === 'qr') ? <DeleteIcon /> : <QrCodeIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={pageItems.some(item => item.type === 'qr') ? () => {
                  // QR kodunu kaldır
                  setPageItems(prev => prev.filter(item => item.type !== 'qr'));
                  setShowQrCode(false);
                } : handleAddQrCode}
              >
                {pageItems.some(item => item.type === 'qr') ? 'QR Kaldır' : 'QR Ekle'}
              </Button>
              
              {pageItems.some(item => item.type === 'qr') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    QR Kod Boyutu:
                  </Typography>
                  <Slider
                    min={80}
                    max={200}
                    step={10}
                    value={qrSize}
                    onChange={(_, value) => {
                      setQrSize(value as number);
                      // Seçili QR kodunun boyutunu güncelle
                      setPageItems(prev => prev.map(item => {
                        if (item.type === 'qr' && item.selected) {
                          return {
                            ...item,
                            size: value as number
                          };
                        }
                        return item;
                      }));
                      // Değişiklikleri kaydet
                      setTimeout(saveLayout, 100);
                    }}
                    valueLabelDisplay="auto"
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Box>

            {/* Logo Ekle Butonu */}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<ImageIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={handleAddLogo}
              >
                Logo Ekle
              </Button>
              
              {/* Gizli dosya girişi */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileUpload}
              />
              
              {pageItems.some(item => item.type === 'logo' && item.selected) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Logo Boyutu:
                  </Typography>
                  <Slider
                    min={50}
                    max={300}
                    step={10}
                    value={pageItems.find(item => item.type === 'logo' && item.selected)?.size || 100}
                    onChange={(_, value) => {
                      // Seçili logonun boyutunu güncelle
                      setPageItems(prev => prev.map(item => {
                        if (item.type === 'logo' && item.selected) {
                          return {
                            ...item,
                            size: value as number
                          };
                        }
                        return item;
                      }));
                      // Değişiklikleri kaydet
                      setTimeout(saveLayout, 100);
                    }}
                    valueLabelDisplay="auto"
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Box>

            {/* Metin Ekle Butonu */}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<TextFieldsIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={handleAddText}
              >
                Metin Ekle
              </Button>
              
              {/* Metin Düzenleme Paneli */}
              {pageItems.some(item => item.type === 'text' && item.selected) && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Metin Düzenleme
                  </Typography>
                  
                  {/* Metin İçeriği */}
                  <TextField
                    fullWidth
                    label="Metin"
                    value={textContent}
                    onChange={handleTextContentChange}
                    margin="dense"
                    size="small"
                    multiline
                    rows={2}
                  />
                  
                  {/* Yazı Tipi */}
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel>Yazı Tipi</InputLabel>
                    <Select
                      value={textFont}
                      label="Yazı Tipi"
                      onChange={handleFontChange}
                    >
                      <MenuItem value="Roboto">Roboto</MenuItem>
                      <MenuItem value="Arial">Arial</MenuItem>
                      <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                      <MenuItem value="Courier New">Courier New</MenuItem>
                      <MenuItem value="Georgia">Georgia</MenuItem>
                      <MenuItem value="Verdana">Verdana</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* Yazı Boyutu */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" gutterBottom>
                      Yazı Boyutu: {textSize}px
                    </Typography>
                    <Slider
                      min={8}
                      max={48}
                      step={1}
                      value={textSize}
                      onChange={handleFontSizeChange}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                  
                  {/* Metin Formatı ve Rengi */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2, gap: 1 }}>
                    {/* Yazı Hizalama - YENİ */}
                    <Typography variant="caption" gutterBottom>
                      Yazı Hizalama
                    </Typography>
                    <ButtonGroup size="small" fullWidth>
                      <Button 
                        onClick={() => handleTextAlign('left')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'left' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignLeftOutlined />
                      </Button>
                      <Button 
                        onClick={() => handleTextAlign('center')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'center' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignCenterOutlined />
                      </Button>
                      <Button 
                        onClick={() => handleTextAlign('right')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'right' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignRightOutlined />
                      </Button>
                    </ButtonGroup>

                    {/* Kalın/İtalik butonları */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" gutterBottom>
                        Yazı Stili
                      </Typography>
                      <Box>
                        <IconButton 
                          size="small" 
                          onClick={handleToggleBold}
                          color={textBold ? "primary" : "default"}
                          sx={{ border: textBold ? '1px solid' : 'none' }}
                        >
                          <Typography sx={{ fontWeight: 'bold' }}>B</Typography>
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={handleToggleItalic}
                          color={textItalic ? "primary" : "default"}
                          sx={{ border: textItalic ? '1px solid' : 'none', ml: 1 }}
                        >
                          <Typography sx={{ fontStyle: 'italic' }}>I</Typography>
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Genişletilmiş Renk Paleti - GENİŞLETİLDİ */}
                    <Typography variant="caption" gutterBottom display="block" sx={{ mt: 1 }}>
                      Yazı Rengi
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {[
                        '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
                        '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#CCFF00', '#66FF00',
                        '#00FF00', '#00FF66', '#00FFCC', '#00FFFF', '#00CCFF', '#0066FF',
                        '#0000FF', '#6600FF', '#CC00FF', '#FF00FF', '#FF00CC', '#FF0066'
                      ].map(color => (
                        <Box
                          key={color}
                          onClick={() => handleColorChange(color)}
                          sx={{
                            width: 18,
                            height: 18,
                            bgcolor: color,
                            borderRadius: '2px',
                            cursor: 'pointer',
                            border: textColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      ))}
                      {/* Özel Renk Seçici */}
                      <Box
                        onClick={() => document.getElementById('custom-color-picker')?.click()}
                        sx={{
                          width: 18,
                          height: 18,
                          border: '1px dashed #aaa',
                          borderRadius: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <span style={{ fontSize: '10px' }}>+</span>
                      </Box>
                      <input
                        type="color"
                        id="custom-color-picker"
                        value={textColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Arkaplan Rengi */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Arkaplan Rengi
              </Typography>
              <Grid container spacing={1}>
                {['#ffffff', '#f5f5f5', '#fafafa', '#eeeeee', '#e0e0e0', '#FFEBEE', '#E3F2FD', '#E8F5E9', '#FFF8E1'].map((color) => (
                  <Grid item xs={4} key={color}>
                    <Box 
                      onClick={() => setPageColor(color)}
                      sx={{
                        width: '100%',
                        height: 40,
                        bgcolor: color,
                        border: pageColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PdfIcon />}
            onClick={handleDownloadQrPdf}
            disabled={!pageItems.some(item => item.type === 'qr')}
          >
            QR PDF İndir
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadQr}
            disabled={!pageItems.some(item => item.type === 'qr')}
          >
            QR İndir
          </Button>
          
          <Button
            variant="outlined"
            color="success"
            startIcon={<VectorIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={handleOpenVectorMenu}
            disabled={!pageItems.some(item => item.type === 'qr')}
            sx={{ 
              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(45deg, #45a049 30%, #7CB342 90%)',
              }
            }}
          >
            Vektörel İndir
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleOpenSaveDialog}
          >
            Tasarımı Kaydet
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FolderOpenIcon />}
            onClick={handleOpenSelectMenu}
            disabled={savedLayouts.length === 0}
          >
            Tasarım Seç
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
            sx={{ minWidth: '150px' }}
          >
            Yazdır
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<DownloadIcon />} 
            onClick={handleSaveImage}
            sx={{ minWidth: '150px' }}
          >
            Bilgisayara Kaydet
          </Button>
        </Box>
      </DialogActions>
      
      {/* Tasarım Kaydetme Dialog'u */}
      <Dialog open={saveLayoutDialogOpen} onClose={() => setSaveLayoutDialogOpen(false)}>
        <DialogTitle>Tasarımı Kaydet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tasarım Adı"
            fullWidth
            value={layoutName}
            onChange={(e) => setLayoutName(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveLayoutDialogOpen(false)}>İptal</Button>
          <Button 
            onClick={handleSaveNamedLayout} 
            color="primary" 
            variant="contained"
            disabled={!layoutName.trim()}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Tasarım Seçme Menüsü */}
      <Menu
        anchorEl={selectLayoutMenuAnchor}
        open={Boolean(selectLayoutMenuAnchor)}
        onClose={() => setSelectLayoutMenuAnchor(null)}
      >
        {savedLayouts.length > 0 ? (
          savedLayouts.map((layout) => (
            <MenuItem 
              key={layout.id} 
              onClick={() => handleLoadLayout(layout.id)}
            >
              {layout.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>Kayıtlı tasarım bulunamadı</MenuItem>
        )}
      </Menu>

      {/* Vektörel İndirme Menüsü */}
      <Menu
        anchorEl={vectorMenuAnchor}
        open={Boolean(vectorMenuAnchor)}
        onClose={() => setVectorMenuAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 200,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              fontSize: '0.95rem',
              fontWeight: 500
            }
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            handleDownloadQrSvg();
            setVectorMenuAnchor(null);
          }}
          sx={{ 
            color: '#4CAF50',
            '&:hover': { 
              backgroundColor: '#E8F5E9',
              color: '#2E7D32'
            }
          }}
        >
          <VectorIcon sx={{ mr: 1.5, fontSize: 20 }} />
          SVG İndir (Sonsuz Kalite)
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleDownloadQrEps();
            setVectorMenuAnchor(null);
          }}
          sx={{ 
            color: '#FF9800',
            '&:hover': { 
              backgroundColor: '#FFF3E0',
              color: '#F57C00'
            }
          }}
        >
          <VectorIcon sx={{ mr: 1.5, fontSize: 20 }} />
          EPS İndir (Profesyonel Baskı)
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleDownloadQrDxf();
            setVectorMenuAnchor(null);
          }}
          sx={{ 
            color: '#2196F3',
            '&:hover': { 
              backgroundColor: '#E3F2FD',
              color: '#1565C0'
            }
          }}
        >
          <VectorIcon sx={{ mr: 1.5, fontSize: 20 }} />
          DXF İndir (CAD/Teknik Çizim)
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleDownloadQrTif();
            setVectorMenuAnchor(null);
          }}
          sx={{ 
            color: '#9C27B0',
            '&:hover': { 
              backgroundColor: '#F3E5F5',
              color: '#7B1FA2'
            }
          }}
        >
          <VectorIcon sx={{ mr: 1.5, fontSize: 20 }} />
          TIF İndir (Ultra HD 4K)
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default QrPrintModal; 